import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DollarSign, TrendingUp, Sparkles, Download, Receipt, Undo2, Trophy } from "lucide-react";

const fmt$ = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDay = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const fmtWeek = (isoDate) => {
  const d = new Date(isoDate + "T00:00:00Z");
  const end = new Date(d); end.setUTCDate(d.getUTCDate() + 6);
  const opts = { month: "short", day: "numeric" };
  return `${d.toLocaleDateString("en-US", opts)} – ${end.toLocaleDateString("en-US", opts)}`;
};

export default function Revenue() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundTx, setRefundTx] = useState(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundBusy, setRefundBusy] = useState(false);

  const load = () => {
    setLoading(true);
    return api.get("/payments/history?weeks=12")
      .then(({ data }) => {
        setData(data);
        // Confetti when the groomer sets a NEW personal best on tips
        try {
          const seen = Number(localStorage.getItem("pawtrek_best_tip_week") || "0");
          const best = data.best_week?.tips || 0;
          if (best > seen && best > 0) {
            localStorage.setItem("pawtrek_best_tip_week", String(best));
            setTimeout(() => {
              confetti({ particleCount: 120, spread: 80, origin: { y: 0.3 },
                colors: ["#C85A17", "#E8A838", "#2D4A3E", "#F5E6CF"] });
              toast.success(`New tip record: $${best.toFixed(2)}!`);
            }, 400);
          }
        } catch (e) { /* ignore */ }
      })
      .catch(() => toast.error("Failed to load payment history"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openRefund = (t) => {
    setRefundTx(t);
    const remaining = Math.max(0, t.total - (t.refunded_amount || 0));
    setRefundAmount(remaining.toFixed(2));
    setRefundOpen(true);
  };

  const submitRefund = async () => {
    if (!refundTx) return;
    const amt = Number(refundAmount);
    if (!amt || amt <= 0) { toast.error("Enter a positive amount"); return; }
    setRefundBusy(true);
    try {
      const { data: r } = await api.post("/payments/refund", {
        session_id: refundTx.session_id,
        amount: amt,
      });
      toast.success(r.full ? "Fully refunded" : `Refunded $${amt.toFixed(2)}`);
      setRefundOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refund failed");
    } finally { setRefundBusy(false); }
  };

  const downloadCsv = async () => {
    try {
      const res = await api.get("/payments/history.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "pawtrek-payments.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed"); }
  };

  if (loading) return <div className="p-6 md:p-8 text-sm text-[#6B625B]">Loading…</div>;

  const maxWeek = Math.max(1, ...(data?.weekly || []).map(w => w.revenue));
  const total = data?.total || { revenue: 0, tips: 0, count: 0 };
  const thisWeek = data?.weekly?.[0] || { revenue: 0, tips: 0, count: 0 };
  const avgTip = total.count ? total.tips / total.count : 0;
  const bestWeek = data?.best_week;

  const partyAgain = () => {
    confetti({ particleCount: 120, spread: 80, origin: { y: 0.3 },
      colors: ["#C85A17", "#E8A838", "#2D4A3E", "#F5E6CF"] });
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-accent">Books</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Revenue</h1>
        </div>
        <Button onClick={downloadCsv} className="rounded-full bg-[#2D4A3E] hover:bg-[#21382E] btn-tactile"
          data-testid="btn-csv-export">
          <Download className="w-4 h-4 mr-1"/> Export CSV
        </Button>
      </header>

      {/* KPIs */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi icon={DollarSign} label="This week" value={fmt$(thisWeek.revenue)} tint="#F5E6CF" fg="#A9480F" tid="kpi-this-week"/>
        <Kpi icon={Sparkles} label="This week tips" value={fmt$(thisWeek.tips)} tint="#FCE7C9" fg="#7A3A09" tid="kpi-week-tips"/>
        <Kpi icon={TrendingUp} label="Last 12 weeks" value={fmt$(total.revenue)} tint="#DDE7E3" fg="#21382E" tid="kpi-total-revenue"/>
        <Kpi icon={Receipt} label="Avg tip / job" value={fmt$(avgTip)} tint="#D8EFDF" fg="#166534" tid="kpi-avg-tip"/>
      </section>

      {/* Best week leaderboard */}
      {bestWeek && (
        <section className="bg-gradient-to-r from-[#2D4A3E] to-[#3D6454] text-white rounded-3xl p-6 shadow-md relative overflow-hidden grain flex items-center justify-between flex-wrap gap-3"
          data-testid="best-week-card">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-[#E8A838] text-[#1F1A17] grid place-content-center shadow">
              <Trophy className="w-7 h-7" strokeWidth={2.2}/>
            </div>
            <div>
              <div className="label-accent text-amber-300">Personal best tips</div>
              <div className="font-display text-3xl font-bold mt-0.5" data-testid="best-week-tips">
                {fmt$(bestWeek.tips)}
              </div>
              <div className="text-xs opacity-80">
                {fmtWeek(bestWeek.week_start)} · {bestWeek.count} job{bestWeek.count===1?"":"s"} · {fmt$(bestWeek.revenue)} revenue
              </div>
            </div>
          </div>
          <Button onClick={partyAgain} variant="outline"
            className="rounded-full border-white/40 text-white hover:bg-white hover:text-[#2D4A3E]"
            data-testid="btn-celebrate">
            <Sparkles className="w-4 h-4 mr-1"/> Celebrate
          </Button>
        </section>
      )}

      {/* Weekly bars */}
      <section className="bg-white rounded-3xl border border-[#E5DCCE] p-6 shadow-sm">
        <h2 className="font-display text-xl font-bold mb-4">Weekly totals</h2>
        {data?.weekly?.length === 0 ? (
          <p className="text-sm text-[#6B625B]">No payments yet — take one for a spin from the client portal.</p>
        ) : (
          <ul className="space-y-3" data-testid="weekly-list">
            {data.weekly.map(w => (
              <li key={w.week_start} className="grid grid-cols-12 items-center gap-3">
                <div className="col-span-4 md:col-span-3 text-xs sm:text-sm text-[#6B625B]">{fmtWeek(w.week_start)}</div>
                <div className="col-span-5 md:col-span-6 relative h-6 bg-[#F3EDE4] rounded-full overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-[#C85A17] rounded-full transition-all"
                    style={{ width: `${(w.revenue / maxWeek) * 100}%` }}/>
                  <div className="absolute inset-y-0 left-0 bg-[#E8A838] rounded-full transition-all opacity-70"
                    style={{ width: `${(w.tips / maxWeek) * 100}%` }}/>
                </div>
                <div className="col-span-3 md:col-span-3 text-right text-sm font-semibold">
                  {fmt$(w.revenue)}
                  <span className="ml-2 text-xs text-[#6B625B]">({w.count} job{w.count===1?"":"s"})</span>
                </div>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-4 flex items-center gap-4 text-xs text-[#6B625B]">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#C85A17]"/> Revenue (incl. tips)</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#E8A838]"/> Tips portion</span>
        </div>
      </section>

      {/* Recent txns */}
      <section className="bg-white rounded-3xl border border-[#E5DCCE] p-6 shadow-sm">
        <h2 className="font-display text-xl font-bold mb-4">Recent payments</h2>
        {data?.transactions?.length === 0 ? (
          <p className="text-sm text-[#6B625B]">Nothing here yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="txns-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-[#6B625B] border-b border-[#E5DCCE]">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Client</th>
                  <th className="py-2 pr-4">Service</th>
                  <th className="py-2 pr-4 text-right">Service</th>
                  <th className="py-2 pr-4 text-right">Tip</th>
                  <th className="py-2 pr-4 text-right">Total</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {data.transactions.map((t, i) => {
                  const remaining = Math.max(0, t.total - (t.refunded_amount || 0));
                  const isRefunded = t.payment_status === "refunded";
                  const isPartial = t.payment_status === "partially_refunded";
                  return (
                    <tr key={i} className="border-b border-[#F3EDE4] last:border-0">
                      <td className="py-2 pr-4 whitespace-nowrap text-[#6B625B]">{fmtDay(t.date)}</td>
                      <td className="py-2 pr-4 whitespace-nowrap">{t.client_name}</td>
                      <td className="py-2 pr-4 whitespace-nowrap">
                        {t.service_name}
                        {t.pet_name && <span className="text-[#6B625B]"> · {t.pet_name}</span>}
                      </td>
                      <td className="py-2 pr-4 text-right">{fmt$(t.service_amount)}</td>
                      <td className="py-2 pr-4 text-right text-[#A9480F]">{fmt$(t.tip)}</td>
                      <td className="py-2 pr-4 text-right font-semibold">
                        {fmt$(t.total)}
                        {(isRefunded || isPartial) && (
                          <div className="text-[10px] font-normal text-rose-600 mt-0.5">
                            {isRefunded ? "Refunded" : `−${fmt$(t.refunded_amount)} refunded`}
                          </div>
                        )}
                      </td>
                      <td className="py-2 text-right">
                        {remaining > 0.01 && (
                          <Button size="sm" variant="ghost" onClick={() => openRefund(t)}
                            className="rounded-full text-rose-600 hover:bg-rose-50"
                            data-testid={`btn-refund-${i}`}>
                            <Undo2 className="w-3.5 h-3.5 mr-1"/> Refund
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Refund modal */}
      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display">Refund payment</DialogTitle></DialogHeader>
          {refundTx && (
            <div className="space-y-3">
              <div className="rounded-2xl bg-[#FAF7F2] border border-[#E5DCCE] p-3 text-sm">
                <div className="font-semibold">{refundTx.client_name}</div>
                <div className="text-xs text-[#6B625B]">
                  {refundTx.service_name}{refundTx.pet_name && ` · ${refundTx.pet_name}`}
                </div>
                <div className="text-xs text-[#6B625B] mt-1">
                  Paid {fmt$(refundTx.total)}
                  {refundTx.refunded_amount > 0 && ` · Already refunded ${fmt$(refundTx.refunded_amount)}`}
                </div>
              </div>
              <div>
                <Label>Amount to refund ($)</Label>
                <Input type="number" min="0" step="0.01" value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)} data-testid="refund-amount"/>
                <div className="text-[10px] text-[#6B625B] mt-1">
                  Enter full remaining amount for a full refund
                </div>
              </div>
              <Button onClick={submitRefund} disabled={refundBusy}
                className="w-full rounded-full bg-rose-600 hover:bg-rose-700"
                data-testid="btn-refund-submit">
                {refundBusy ? "Refunding…" : `Refund $${Number(refundAmount || 0).toFixed(2)}`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tint, fg, tid }) {
  return (
    <div className="bg-white rounded-3xl border border-[#E5DCCE] p-5 card-tactile" data-testid={tid}>
      <div className="w-10 h-10 rounded-xl grid place-content-center mb-3" style={{ background: tint, color: fg }}>
        <Icon className="w-5 h-5" strokeWidth={2.2}/>
      </div>
      <div className="text-xs uppercase tracking-wider text-[#6B625B] font-semibold">{label}</div>
      <div className="font-display text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
